import { Typography, Box, Divider } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import DeliveryDetails from "./DeliveryDetails"
import ContactDetails from "./ContactDetails"
import OrderItemsTable from "./OrderItemsTable"
import PackageSlipHeader from "./PackageSlipHeader"
import { MeetingDetailsOrder } from "../../../../../../nonAdminUser/reports/home/meetingServiceApi"
import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { ErrorBoundary } from "react-error-boundary"
import { StandardAlertInfo } from "../../../../../../../shared/components/alert/StandardAlertInfo"

const useStyles = makeStyles()((theme) => ({
    orderContainer: {
        padding: "3.875rem",
        [theme.breakpoints.down("sm")]: {
            padding: "2rem 1.5rem",
            boxShadow: "none",
            width: "100%",
        },
        height: "fit-content",
        backgroundColor: "white",
        boxShadow: "0px 0px 10px 0px #00000026;",
        "&:last-child": { marginBottom: 0 },
        "@media print": {
            marginBottom: 0,
            paddingTop: "1.5rem",
            paddingBottom: 0,
            paddingLeft: "1.5rem",
            paddingRight: "1.5rem",
            width: "100%",
            boxShadow: "none",
            backgroundColor: "white",
            pageBreakInside: "avoid",
            breakInside: "avoid",
            pageBreakAfter: "always",
            breakAfter: "page",
            "&:last-child": {
                pageBreakAfter: "auto",
                breakAfter: "auto",
            },
        },
    },
    errorTitle: {
        fontWeight: 600,
        marginBottom: "1rem",
    },
    orderDivider: {
        borderColor: theme.palette.divider,
    },
    orderInfo: {
        marginBottom: theme.spacing(4),
        [theme.breakpoints.down("sm")]: {
            marginBottom: theme.spacing(3),
        },
    },
    orderId: {
        fontWeight: 600,
        color: theme.palette.tailwind.slate[700],
        lineHeight: "1.5rem",
        marginBottom: theme.spacing(0.5),
        [theme.breakpoints.down("sm")]: {
            fontWeight: 500,
            fontSize: "1.25rem",
            marginBottom: theme.spacing(0),
            color: theme.palette.tailwind.slate[800],
        },
    },
    orderName: {
        fontWeight: 400,
        fontSize: "1.125rem",
        lineHeight: "1.5rem",
        color: theme.palette.tailwind.slate[500],
        [theme.breakpoints.down("sm")]: {
            fontSize: "1rem",
        },
    },
    mainContent: {
        display: "flex",
        flexWrap: "nowrap",
        gap: "100px",
        marginTop: theme.spacing(5),
        [theme.breakpoints.down("md")]: {
            flexDirection: "column",
            gap: theme.spacing(2.5),
            marginTop: "3.25rem",
        },
        "@media print": {
            display: "flex",
            flexDirection: "row",
            gap: "100px",
            minWidth: 0,
            maxWidth: "100%",
            boxSizing: "border-box",
            flexWrap: "nowrap",
            alignItems: "flex-start",
            pageBreakInside: "auto",
            breakInside: "auto",
        },
    },
    leftColumn: {
        flex: "0 0 35%",
        maxWidth: "35%",
        [theme.breakpoints.down("md")]: {
            flex: "1 1 100%",
            maxWidth: "100%",
        },
        "@media print": {
            flex: "0 0 35%",
            maxWidth: "35%",
            boxSizing: "border-box",
            pageBreakInside: "auto",
            breakInside: "auto",
        },
    },
    rightColumn: {
        flex: 1,
        minWidth: 0,
        [theme.breakpoints.down("md")]: {
            flex: "1 1 100%",
            maxWidth: "100%",
        },
        "@media print": {
            flex: 1,
            minWidth: 0,
            boxSizing: "border-box",
            pageBreakInside: "auto",
            breakInside: "auto",
        },
    },
    tableDivider: {
        [theme.breakpoints.up("md")]: {
            display: "none",
        },
    },
    hidePrint: {
        "@media print": {
            display: "none",
        },
    },
    title: {
        fontWeight: 500,
        color: theme.palette.tailwind.slate[700],

        [theme.breakpoints.down("sm")]: {
            textAlign: "center",
            fontSize: theme.typography.h5.fontSize,
            lineHeight: 1.2,
            fontWeight: 700,
        },
    },
    errorDetails: {
        marginTop: "2rem",
    },
    noteSection: {
        marginTop: theme.spacing(2),
    },
    sectionTitle: {
        fontWeight: 500,
        color: theme.palette.tailwind.slate[700],
        marginBottom: theme.spacing(0.5),
    },
}))

interface SingleOrderProps {
    order: MeetingDetailsOrder
}

function ErrorFallback({ orderId, message, title }: { orderId: number; message?: string; title?: string | null }) {
    const { classes, cx } = useStyles()

    return (
        <Box className={cx(classes.orderContainer)}>
            <LocalizedStrict id="production-package-slip-section-header-slip-title">
                <Typography variant="h4" component="h1" className={classes.title}>
                    Packing slip
                </Typography>
            </LocalizedStrict>
            <Box className={classes.errorDetails}>
                {title !== null &&
                    (title ? (
                        <Typography variant="h5" className={classes.errorTitle}>
                            {title}
                        </Typography>
                    ) : (
                        <LocalizedStrict
                            id="production-package-slip-order-error"
                            vars={{ orderId: orderId.toString() }}
                        >
                            <Typography variant="h5" className={classes.errorTitle}>
                                Failed to load packing slip with id #{orderId.toString()}
                            </Typography>
                        </LocalizedStrict>
                    ))}
                {message ? (
                    <Typography variant="body1">{message}</Typography>
                ) : (
                    <LocalizedStrict id="production-package-slip-order-error-description">
                        <Typography variant="body1">
                            This order could not be processed due to missing or invalid data.
                        </Typography>
                    </LocalizedStrict>
                )}
            </Box>
        </Box>
    )
}

export default function SingleOrder({ order }: SingleOrderProps) {
    const { classes, cx } = useStyles()
    const orderLines = order.deliveries.flatMap((delivery) =>
        delivery.orderLines.map((line) => ({ ...line, deliveryTime: line.deliveryTime ?? delivery.deliveryTime })),
    )

    return order.serviceResponse ? (
        <ErrorFallback orderId={order?.id} message={order.serviceResponse.displayMessage} title={null} />
    ) : (
        <ErrorBoundary FallbackComponent={() => <ErrorFallback orderId={order?.id} />}>
            <Box className={classes.orderContainer}>
                <PackageSlipHeader order={order} />
                <Box className={classes.mainContent}>
                    <Box className={classes.leftColumn}>
                        <Box className={classes.orderInfo}>
                            <LocalizedStrict
                                id="production-package-slip-order-id-number"
                                vars={{ orderId: order?.id.toString() }}
                            >
                                <Typography variant="h6" className={classes.orderId}>
                                    Order ID: {order?.id.toString()}
                                </Typography>
                            </LocalizedStrict>
                            <Typography variant="body1" className={classes.orderName}>
                                {order?.meetingDetails.meetingName}
                            </Typography>
                        </Box>
                        <Divider />
                        <DeliveryDetails order={order} />
                        <Divider className={classes.orderDivider} />
                        <ContactDetails order={order} />
                    </Box>
                    <Box className={classes.rightColumn}>
                        <Divider className={cx([classes.orderDivider, classes.tableDivider])} />
                        <OrderItemsTable orders={orderLines} />
                        {!!order.orderNote && (
                            <Box className={classes.noteSection}>
                                <LocalizedStrict id="production-package-slip-section-note-title">
                                    <Typography variant="h6" className={classes.sectionTitle}>
                                        Note
                                    </Typography>
                                </LocalizedStrict>
                                <StandardAlertInfo title={order.orderNote} icon={false} />
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
        </ErrorBoundary>
    )
}
